/**
 * 008-reporting-dashboards T015-T017 (US2, FR-008 to FR-011, FR-019). Same shape as
 * `StockMovement.tsx`/`../invoicing/GstSummary.tsx`: a from/to-bounded, unpaged, client-exported
 * report — period form driving `searchParams`, fetch-on-submit-or-bookmarked-load, `ExportButton`
 * fed rows mapped to plainly-keyed objects in the exact header order (`flattenObject`'s
 * `for...in` key-order hazard, see `GstSummary.tsx`'s own comment).
 *
 * `GET /api/warehouse/reports/variance` takes only `from`/`to` — no `packer`/`sku`/`reasonCode`
 * filter param exists on the real route (verified against `ReportController.GetVarianceReport`);
 * the three breakdowns below come from the response's own `byPacker`/`bySku`/`byReasonCode`
 * arrays. Packer ids render raw: `GET /api/identity/users/{id}` needs `company.user.administer`,
 * which a reports-only role plausibly won't hold, so no name-resolution call is made here.
 *
 * Gated on `packing.oversight` (008-reporting-dashboards T037, contracts/screen-routes.md) — was
 * `inventory.read` (matching `dashboard.tsx`'s own gate on this endpoint, T009) before T037 added a
 * dedicated code for this feature's own report routes.
 */
import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { PiCubeDuotone, PiPercentDuotone, PiScalesDuotone } from "react-icons/pi";
import { Button } from "rizzui";
import { WidgetCard, ErrorSurface, EmptyState, ExportButton, MetricCard } from "@steelyard/theme";
import { DateField } from "../../components/date-field";
import { ListHeader } from "../../components/table/list-header";
import { ReportTable } from "../../components/table/table-view";
import { can } from "../../lib/permissions";
import { formatDate, formatMoment, parseIsoDate, toIsoDate } from "../../lib/format";
import {
  getVarianceReport,
  bandLabel,
  formatPercent,
  personName,
  reasonCodeLabel,
  type VarianceReportView,
  type PackerVarianceRow,
  type SkuVarianceRow,
  type ReasonCodeRow,
  type VarianceTrendPoint,
  type VarianceEventRow,
} from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";
import { packerVarianceColumns, skuVarianceColumns, reasonCodeVarianceColumns, trendColumns, varianceEventColumns } from "./variance-columns";


/**
 * Each section is a `ReportTable` — paged in memory, not `PagedTable`: this screen exports
 * (`ExportButton`), and `export-guard.test.ts` forbids pairing the two. The export still gets every
 * row — it maps `report.events`, never the visible page. Each section keys on `producedAt`, so a new
 * fetch starts it from its first page.
 */

export default function VarianceReportPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<VarianceReportView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);

  // Rules-of-hooks: called unconditionally, ahead of the permission-gate return below.
  useEffect(() => {
    if (from && to) run(from, to);
  }, [from, to]);

  if (!can(permissionState, "packing.oversight")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403, detail: "The variance report is gated by packing.oversight, which your role does not hold." }} action="view the variance report" />;
  }

  async function run(nextFrom: string, nextTo: string) {
    setLoading(true);
    setError(null);
    try {
      setReport(await getVarianceReport({ from: nextFrom, to: nextTo }));
    } catch (err) {
      setError(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!fromDate || !toDate) return; // matches the removed native `required`'s own submit-blocking
    setSearchParams({ from: fromDate, to: toDate });
    run(fromDate, toDate);
  }

  const header = (
    <ListHeader
      title="Packing Variance Report"
      description="Amber and red packing events over a period, broken down by who packed each line, by SKU and by reason, with the weekly trend and every underlying event."
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Inventory" }, { name: "Variance Report" }]}
    >
        {report && !report.empty ? (
          <ExportButton
            // Events only (FR-019's rawest row-for-row match) — mapped in the EXACT displayed
            // column order, not passed as VarianceEventRow[] directly (flattenObject's for...in
            // key-order hazard, see GstSummary.tsx's own comment).
            data={report.events.map((r) => ({
              Captured: formatMoment(r.capturedAt),
              Synced: formatMoment(r.syncedAt),
              Packer: personName(r.packerName),
              Order: r.orderNumber ?? "",
              SKU: r.skuName,
              Band: bandLabel(r.band),
              Reason: r.reasonCode ? reasonCodeLabel(r.reasonCode) : "",
              Note: r.reasonNote ?? "",
              "Override by": r.overrideBy ? personName(r.overrideByName) : "",
              "Override reason": r.overrideReason ?? "",
            }))}
            header="Captured,Synced,Packer,Order,SKU,Band,Reason,Note,Override by,Override reason"
            fileName={`variance-${from}-to-${to}`}
          />
        ) : null}
    </ListHeader>
  );

  const periodForm = (
    <WidgetCard title="Period" rounded="lg" headerClassName="border-muted border-b pb-4">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4 pt-4">
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="From…"
          selected={parseIsoDate(fromDate || null)}
          maxDate={parseIsoDate(toDate || null) ?? undefined}
          onChange={(date) => setFromDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "From", clearable: Boolean(fromDate), onClear: () => setFromDate("") }}
        />
        <DateField
          dateFormat="dd-MMM-yyyy"
          className="w-44"
          placeholderText="To…"
          selected={parseIsoDate(toDate || null)}
          minDate={parseIsoDate(fromDate || null) ?? undefined}
          onChange={(date) => setToDate(date ? toIsoDate(date as Date) : "")}
          inputProps={{ label: "To", clearable: Boolean(toDate), onClear: () => setToDate("") }}
        />
        <Button type="submit" disabled={loading}>{loading ? "Running…" : "Run the report"}</Button>
      </form>
    </WidgetCard>
  );

  if (!from || !to) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        {periodForm}
        <EmptyState title="Choose a period">The report runs over a bounded range you choose, same as the stock movement report.</EmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {header}
      {periodForm}

      {error ? <ErrorSurface error={error} action="run the variance report" /> : null}

      {report ? (
        report.empty ? (
          <WidgetCard title={<>{formatDate(from)} to {formatDate(to)}</>} rounded="lg" headerClassName="border-muted border-b">
            <EmptyState title="No packing in this period">
              {report.emptyReason ? reasonCodeLabel(report.emptyReason) + "." : "Nothing was packed in the chosen range."}
            </EmptyState>
          </WidgetCard>
        ) : (
          <>
            {/* The API's own three period figures, each as sent — none derived from the rows below. */}
            <div className="grid gap-4 sm:grid-cols-3">
              <MetricCard title="Lines packed" metric={report.linesPacked} icon={<PiCubeDuotone aria-hidden className="size-5" />} rounded="lg" />
              <MetricCard title="Variance rate" metric={formatPercent(report.varianceRate)} icon={<PiPercentDuotone aria-hidden className="size-5" />} rounded="lg" />
              <MetricCard title="Collision confirmations" metric={report.collisionConfirmations} icon={<PiScalesDuotone aria-hidden className="size-5" />} rounded="lg" />
            </div>

            <ReportTable<PackerVarianceRow>
              key={`${report.producedAt}-by-who-packed`}
              title="By packing operative"
              rows={report.byPacker}
              columns={packerVarianceColumns}
              caption="Variance by who packed each line"
              searchPlaceholder="Search packers…"
            />
            <ReportTable<SkuVarianceRow>
              key={`${report.producedAt}-sku`}
              title="By SKU"
              rows={report.bySku}
              columns={skuVarianceColumns}
              caption="Variance by SKU"
              searchPlaceholder="Search SKUs…"
            />
            <ReportTable<ReasonCodeRow>
              key={`${report.producedAt}-reason`}
              title="By reason code"
              rows={report.byReasonCode}
              columns={reasonCodeVarianceColumns}
              caption="Variance by reason code"
            />
            <ReportTable<VarianceTrendPoint>
              key={`${report.producedAt}-trend`}
              title="Trend"
              rows={report.trend}
              columns={trendColumns(report.trend)}
              caption="Weekly variance trend"
            />
            <ReportTable<VarianceEventRow>
              key={`${report.producedAt}-events`}
              title="Events"
              description={`${report.events.length} ${report.events.length === 1 ? "row" : "rows"}, every amber and red packing event in the period.`}
              rows={report.events}
              columns={varianceEventColumns}
              caption="Every amber and red packing event in the period"
              searchPlaceholder="Search events…"
            />
          </>
        )
      ) : null}
    </div>
  );
}
